/**
 * Keycloak Location Validation Utility
 * 
 * This module determines whether Keycloak authentication should be enabled
 * based on the current location (hostname and port).
 * 
 * ALLOWED LOCATIONS:
 * - localhost:8415 (development)
 * - smpservices-dev.eastus.cloudapp.azure.com (development Azure environment)
 * 
 * For all other locations, Keycloak parameter should be ignored and
 * the application should default to Okta authentication.
 */

/**
 * List of allowed hostnames and ports where Keycloak can be activated
 */
const ALLOWED_KEYCLOAK_LOCATIONS = [
    {
        hostname: 'localhost',
        port: '8415'
    },
    {
        hostname: 'smpservices-dev.eastus.cloudapp.azure.com',
        port: null // Any port or default port (80/443)
    }
];

/**
 * Check if the current location is allowed to use Keycloak
 * 
 * @param {string} [locationHostname] - Optional hostname to check (defaults to window.location.hostname)
 * @param {string} [locationPort] - Optional port to check (defaults to window.location.port)
 * @returns {boolean} - True if Keycloak is allowed at this location, false otherwise
 */
export const isKeycloakAllowedLocation = (
    locationHostname = window.location.hostname,
    locationPort = window.location.port
) => {
    // Check if current location matches any allowed location
    return ALLOWED_KEYCLOAK_LOCATIONS.some(allowedLocation => {
        const hostnameMatches = allowedLocation.hostname === locationHostname;
        
        // If port is not specified in allowed location, any port is accepted
        // Otherwise, port must match exactly
        const portMatches = allowedLocation.port === null || 
                          allowedLocation.port === locationPort ||
                          // Handle case where port is empty string (default port)
                          (allowedLocation.port === locationPort && locationPort === '');
        
        return hostnameMatches && portMatches;
    });
};

/**
 * Check if Keycloak should be activated based on URL parameter AND location
 * 
 * This is the primary function that should be used throughout the application
 * to determine if Keycloak mode should be enabled.
 * 
 * @param {URLSearchParams} [urlParams] - Optional URL search params (defaults to current window location)
 * @returns {boolean} - True if Keycloak should be activated, false otherwise
 */
export const shouldActivateKeycloak = (urlParams = new URLSearchParams(window.location.search)) => {
    // First, check if the keycloak parameter is present and set to true/1
    const keycloakParam = urlParams.get('keycloak');
    const hasKeycloakParam = keycloakParam === 'true' || keycloakParam === '1';
    
    // If keycloak parameter is not present or not enabled, return false
    if (!hasKeycloakParam) {
        return false;
    }
    
    // If keycloak parameter is present, check if we're at an allowed location
    const isAllowedLocation = isKeycloakAllowedLocation();
    
    return isAllowedLocation;
};

/**
 * Get user-friendly message about Keycloak availability
 * 
 * @returns {string} - Message explaining whether Keycloak is available at current location
 */
export const getKeycloakAvailabilityMessage = () => {
    if (isKeycloakAllowedLocation()) {
        return 'Keycloak authentication is available at this location.';
    }
    
    const allowedLocationsStr = ALLOWED_KEYCLOAK_LOCATIONS.map(loc => 
        loc.port ? `${loc.hostname}:${loc.port}` : loc.hostname
    ).join(', ');
    
    return `Keycloak authentication is not available at this location. ` +
           `Allowed locations: ${allowedLocationsStr}`;
};
